import { NANGO_HOST, REGIONS } from 'v2/configs/constants'
import { getLocalAuth } from 'v2/utils'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { getSafeIntegrationErrorMetadata } from './utils/safe-error-metadata'

// sessionStorage key linking the redirect-out (here) and redirect-back
// (AciOAuthLanding) sides of the bridge OAuth round-trip.
export const ACI_RETURN_TO_KEY = 'aci_return_to'

/**
 * Redirect the browser to a backend-provided OAuth start URL (bridge flow).
 *
 * The plivocx V2 endpoints return `oauth_redirect_url` — the bridge's OAuth start
 * URL, whose linked_account_owner_id == the new org_app uuid. We navigate the SPA
 * to it so the user lands on the provider consent screen; after consent the bridge
 * stores the linked account and redirects back to the console.
 */
export const redirectToOAuth = (url: string): void => {
  // Capture where the connect was initiated so the OAuth landing page
  // (/integrations) can return the user here after it finalizes the connection.
  // Do NOT overwrite an already-set return-to: a caller that navigated here from
  // elsewhere (e.g. the KB-create "Connect now" banner) stashed its own origin first,
  // and this page's own redirect must preserve it rather than clobber it with the
  // integrations page.
  try {
    if (!sessionStorage.getItem(ACI_RETURN_TO_KEY)) {
      sessionStorage.setItem(ACI_RETURN_TO_KEY, window.location.pathname + window.location.search)
    }
  } catch {
    // ignore storage write failures (private mode, etc.)
  }
  window.location.assign(url)
}

interface InitiateNangoAuthArgs {
  provider: string
  connection: Record<string, unknown>
  credentials: Record<string, unknown>
  sessionToken: string
  appName: string
}

/**
 * initiateNangoAuth toasts its own auth failure before rethrowing (it always
 * has — this mirrors main). Callers that wrap it in a toasting mutation must
 * check this so the user does not get a second error toast for the same
 * failure.
 */
export const isNangoAuthToastedError = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === 'object' &&
      (error as { nangoAuthToasted?: boolean }).nangoAuthToasted === true,
  )

const markNangoAuthToasted = (error: unknown): void => {
  if (error && typeof error === 'object') {
    ;(error as { nangoAuthToasted?: boolean }).nangoAuthToasted = true
  }
}

export const initiateNangoAuth = async ({
  provider,
  connection,
  credentials,
  sessionToken,
  appName,
}: InitiateNangoAuthArgs): Promise<void> => {
  try {
    const { default: Nango } = await import('@nangohq/frontend')
    const auth = getLocalAuth()
    const region = auth.region === REGIONS.US ? 'use1' : 'aps1'
    const nango = new Nango({
      host: NANGO_HOST.replace('{{region}}', region),
      websocketsPath: '/',
      connectSessionToken: sessionToken,
    })

    try {
      const authParams = { params: connection || {}, credentials: credentials || {} }
      await nango.auth(provider, authParams)
    } catch (error: unknown) {
      logger.error('Nango authentication failed', getSafeIntegrationErrorMetadata(error))
      errorToast({
        title: `Failed to authenticate with ${appName}`,
        description: 'Authentication did not complete. Please try again.',
      })
      markNangoAuthToasted(error)
      // Re-throw or handle appropriately if needed upstream
      throw error // Or return a status object
    }
  } catch (error: unknown) {
    logger.error('Integration error during Nango setup', getSafeIntegrationErrorMetadata(error))
    // Re-throw or handle appropriately if needed upstream
    throw error // Or return a status object
  }
}
